import { Skeleton, cn } from '@parcel/ui';
import { ArrowUpRight, FileText, Globe, type LucideIcon, Sparkles, UserCheck } from 'lucide-react';
import * as React from 'react';
import { logoUrl } from '../../lib/logo.js';
import type { ApiEnrichmentMention, ApiMention } from '../../lib/mentions.js';
import { mentionRoleMeta } from '../../lib/mentions.js';
import { Hint } from '../hint.js';
import { ExpandableText } from './expandable-text.js';
import { MentionSectionReader } from './mention-section-reader.js';

// Evidence grouped by kind (filings, web pages, research), each item leading with where it came from.

const PANEL = 'rounded-lg border bg-muted/30 px-3.5 py-3';

interface ProjectEvidenceBodyProps {
  enrichment: ApiEnrichmentMention[];
  mentions: ApiMention[];
  isLoading: boolean;
  /** The record the evidence is about; a web quote that is only this name is not shown. */
  entityName?: string;
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  // Calendar dates are stored at midnight UTC; format in UTC so no viewer sees the day before.
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });
}

/** Only a doubt is worth reading: "medium" → "Medium confidence"; high or empty → null. */
function formatDoubt(confidence: string): string | null {
  const trimmed = confidence.trim().toLowerCase();
  if (!trimmed || trimmed === 'high') return null;
  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)} confidence`;
}

/** Bare SIRE abbreviations stored on some documents, expanded for display. */
const DOC_TYPE_ABBREVIATIONS: Record<string, string> = {
  LOI: 'Letter of Intent (LOI)',
  PNF: 'Project Notification Form (PNF)',
  SPRA: 'Small Project Review Application (SPRA)',
  NPC: 'Notice of Project Change (NPC)',
  SPC: 'Small Project Change (SPC)',
  DPIR: 'Draft Project Impact Report (DPIR)',
  FPIR: 'Final Project Impact Report (FPIR)',
};

/** "LOI" → "Letter of Intent (LOI)"; "project_narrative" → "Project narrative"; display strings pass through. */
function formatDocumentType(documentType: string | null): string | null {
  const trimmed = documentType?.trim();
  if (!trimmed) return null;
  const expanded = DOC_TYPE_ABBREVIATIONS[trimmed.toUpperCase()];
  if (expanded) return expanded;
  if (trimmed.includes('_')) {
    const words = trimmed.replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return trimmed;
}

// Open-web page classes; everything else is a filing.
const WEB_PAGE_LABELS: Readonly<Record<string, string>> = {
  news: 'News',
  press_release: 'Press release',
  firm_site: "Firm's site",
  official_project_site: 'Project site',
  government_page: 'Government page',
};

function hostname(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

const normalize = (value: string | null) => (value ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
const sameName = (a: string, b: string) => normalize(a) === normalize(b);
// A quote that is only (part of) the record's own name says nothing the row doesn't; any other words keep it.
const words = (value: string | null) =>
  normalize(value)
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
function isBareName(excerpt: string | null, entityName: string | undefined): boolean {
  const quote = words(excerpt);
  if (!quote || !entityName) return !quote;
  return ` ${words(entityName)} `.includes(` ${quote} `);
}

function dedupe<T>(items: T[], key: (item: T) => string): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const k = key(item);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function SiteIcon({ host }: { host: string | null }) {
  const [failed, setFailed] = React.useState(false);
  const src = host ? logoUrl(host) : null;
  if (!src || failed) return <TileIcon icon={Globe} />;
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-background">
      <img src={src} alt="" className="h-4 w-4 object-contain" onError={() => setFailed(true)} />
    </span>
  );
}

function TileIcon({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border bg-background text-muted-foreground">
      <Icon className="h-3.5 w-3.5" aria-hidden />
    </span>
  );
}

function OpenLink({ href }: { href: string }) {
  return (
    <Hint label="View original">
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label="View original"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-background hover:text-foreground"
      >
        <ArrowUpRight className="h-4 w-4" aria-hidden />
      </a>
    </Hint>
  );
}

function Item({
  icon,
  title,
  meta,
  href,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  meta: string;
  href?: string | null;
  children?: React.ReactNode;
}) {
  const heading = [title, meta].filter(Boolean).join(' · ');
  return (
    <li className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
      {icon}
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <Hint label={heading}>
            <div className="min-w-0 flex-1" data-heading={heading}>
              <div className="truncate text-sm font-medium text-foreground">{title}</div>
              {meta ? <div className="truncate text-xs text-muted-foreground">{meta}</div> : null}
            </div>
          </Hint>
          {href ? <OpenLink href={href} /> : null}
        </div>
        {children ? <div className="mt-1.5">{children}</div> : null}
      </div>
    </li>
  );
}

function Group({
  label,
  count,
  children,
}: { label: string; count: number; children: React.ReactNode }) {
  if (count === 0) return null;
  return (
    <section data-evidence-group={label} className="space-y-2">
      <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        {label}
        <span className="rounded-full bg-background px-1.5 tabular-nums ring-1 ring-border">
          {count}
        </span>
      </h4>
      <ul className="divide-y">{children}</ul>
    </section>
  );
}

export function ProjectEvidenceBody({
  enrichment,
  mentions,
  isLoading,
  entityName,
}: ProjectEvidenceBodyProps) {
  if (isLoading) {
    return (
      <div className={cn(PANEL, 'space-y-3')} aria-busy="true" aria-label="Loading evidence">
        <Skeleton className="h-3 w-20" />
        {[0, 1].map((k) => (
          <div key={k} className="flex items-start gap-3">
            <Skeleton className="h-7 w-7 shrink-0 rounded-md" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-48" />
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-3 w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (enrichment.length === 0 && mentions.length === 0) {
    return (
      <p className={cn(PANEL, 'text-sm text-muted-foreground')}>
        No source mentions found for this project.
      </p>
    );
  }

  // Customer self-reports have no document behind them, so they read as their own kind.
  const selfReported = mentions.filter((m) => m.source_adapter === 'customer_feedback');
  const documents = mentions.filter((m) => m.source_adapter !== 'customer_feedback');
  const filings = dedupe(
    documents.filter((m) => !WEB_PAGE_LABELS[m.source.document_type ?? '']),
    (m) =>
      [
        m.source.document_id ?? m.source.source_url,
        normalize(m.evidence_excerpt),
        normalize(m.section?.text ?? null),
      ].join('|'),
  );
  // The same line on the same site is one page, whichever copy we read it from.
  const webPages = dedupe(
    documents.filter((m) => WEB_PAGE_LABELS[m.source.document_type ?? '']),
    (m) =>
      [
        hostname(m.source.source_url) ?? m.source.source_display_name,
        normalize(m.evidence_excerpt),
      ].join('|'),
  );
  // The row already shows the role; name it per filing only when the filings disagree.
  const showRole = new Set(filings.map((m) => m.role)).size > 1;

  return (
    <div className={cn(PANEL, 'space-y-4')}>
      <Group label="Self-reported" count={selfReported.length}>
        {selfReported.map((m, i) => (
          <Item
            key={`report-${i}-${m.role}`}
            icon={<TileIcon icon={UserCheck} />}
            title="Self-reported"
            meta={[mentionRoleMeta(m.role).label, 'Customer report'].join(' · ')}
          >
            {m.evidence_excerpt ? (
              <p className="text-[13px] leading-5 text-muted-foreground">{m.evidence_excerpt}</p>
            ) : null}
          </Item>
        ))}
      </Group>
      <Group label="Filings" count={filings.length}>
        {filings.map((m, i) => {
          const role = mentionRoleMeta(m.role);
          const type = formatDocumentType(m.source.document_type);
          const title = type ?? m.source.source_display_name ?? 'Source filing';
          const meta = [
            showRole ? role.label : null,
            type ? m.source.source_display_name : null,
            formatDate(m.source.document_date),
            formatDoubt(m.confidence),
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <Item
              key={`filing-${i}-${m.source.document_id ?? m.role}`}
              icon={<TileIcon icon={FileText} />}
              title={title}
              meta={meta}
              href={m.source.source_url}
            >
              <MentionSectionReader
                sectionText={m.section?.text ?? null}
                excerpt={m.evidence_excerpt}
                variant={role.variant}
              />
            </Item>
          );
        })}
      </Group>
      <Group label="Web pages" count={webPages.length}>
        {webPages.map((m, i) => {
          const host = hostname(m.source.source_url);
          const meta = [
            WEB_PAGE_LABELS[m.source.document_type ?? ''],
            formatDate(m.source.document_date),
            formatDoubt(m.confidence),
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <Item
              key={`web-${i}-${host}`}
              icon={<SiteIcon host={host} />}
              title={host ?? m.source.source_display_name ?? 'Web page'}
              meta={meta}
              href={m.source.source_url}
            >
              {/* A page's surrounding text is navigation chrome, so only a real sentence is quoted. */}
              {isBareName(m.evidence_excerpt, entityName) ? null : (
                <p className="line-clamp-2 text-[13px] leading-5 text-muted-foreground">
                  {`“${m.evidence_excerpt?.replace(/\s+/g, ' ').trim()}”`}
                </p>
              )}
            </Item>
          );
        })}
      </Group>
      {enrichment.length > 0 ? <ResearchGroup enrichment={enrichment} /> : null}
    </div>
  );
}

/** Every research match folds into one entry: the filed names it matched, the latest reasoning, the sites read. */
function ResearchGroup({ enrichment }: { enrichment: ApiEnrichmentMention[] }) {
  const latest = [...enrichment].sort((a, b) =>
    (b.as_of_date ?? '').localeCompare(a.as_of_date ?? ''),
  )[0] as ApiEnrichmentMention;
  const resolved = latest.resolved_name;
  const filedNames = dedupe(
    enrichment.map((e) => e.entity_string.trim()).filter((name) => !sameName(name, resolved)),
    normalize,
  );
  const reasoning = latest.reasoning ?? enrichment.find((e) => e.reasoning)?.reasoning ?? null;
  const sites = dedupe(
    enrichment.flatMap((e) => e.evidence_urls),
    (url) => url,
  );
  const asOf = formatDate(latest.as_of_date);
  const meta = [
    asOf ? `as of ${asOf}` : null,
    enrichment.some((e) => e.source === 'human_override') ? 'Confirmed by you' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Group label="Research" count={1}>
      <Item
        icon={<TileIcon icon={Sparkles} />}
        title={
          filedNames.length
            ? `Matched ${filedNames.length} filed ${filedNames.length === 1 ? 'name' : 'names'}`
            : 'Web research'
        }
        meta={meta}
      >
        {filedNames.length ? (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {filedNames.map((name) => (
              <span
                key={name}
                className="max-w-full truncate rounded-md border bg-background px-1.5 py-0.5 text-xs text-foreground"
              >
                {name}
              </span>
            ))}
          </div>
        ) : null}
        {reasoning ? (
          <ExpandableText
            text={reasoning}
            collapsedLines={2}
            className="text-[13px] leading-5 text-muted-foreground"
            toggleClassName="ml-0 mt-1 text-xs text-muted-foreground hover:text-foreground"
          />
        ) : null}
        {sites.length ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {sites.map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-md border bg-background px-1.5 py-0.5 text-xs text-foreground/80 hover:text-foreground"
              >
                {hostname(url) ?? url}
                <ArrowUpRight className="h-3 w-3" aria-hidden />
              </a>
            ))}
          </div>
        ) : null}
      </Item>
    </Group>
  );
}
